import type { AdobeApp, ApplicationContext, ElectronContext } from '@mimir/common/context/applicationContext';
import { hasAdobePanel, isPremiere, isAe, isPs, isAudition } from '../premiere/cep';
import { getNodeApi } from '@mimir/common/util/detectApi';
import { CepBridgeClient } from '../premiere/cepBridgeClient';
import { DvrClient } from '../dvr/dvrClient';
import { globalBackendClient } from '@mimir/client-common/globals/backendClient';
import { createUiDefaultDownloader } from './createUiDefaultDownloader';
import { LocalCallbackBasedVfs } from '@mimir/common/nodejs/localFs';
import { makeNodeJsHighPrecisionMonotonicClock } from '@mimir/common/nodejs/nodeHighPrecisionMonotonicClock';
import type { Vfs } from '@mimir/common/types/vfs';
import { Lock } from '@mimir/common/async/lock';
import { createUuid } from '@mimir/common/util/createUuid';
import { desktopState } from '../globals/desktop';
import { getIpcNodeApi, type DesktopMainProcessRequireType, type IpcRendererLogger } from '@mimir/common/desktop/ipc';
import { needPrematureVideoFrameReuseBugWorkaroundFromBrowserContext } from '@mimir/browser/gldraw/browserQuirks';
import { createIpcVfsWrapper } from '../desktop/ipcVfs';
import { initVfsPortClient } from '../desktop/ipcVfsPort';
import { invariant } from '@mimir/common/util/invariant';

const getPathSep = (isWin: boolean) => isWin ? '\\' : '/';

/**
 * Returns an object describing the context the application runs in.
 * It is meant to be invoked exactly once during bootstrap and then
 * passed down to all the components that need it.
 */
export function getApplicationContext(): ApplicationContext {
  // TODO: Ideally we should inject the dependencies to create a backend
  // client here
  const backendClient = globalBackendClient;

  if (hasAdobePanel()) {
    let app: AdobeApp = 'unknown';
    if (isPremiere()) {
      app = 'Premiere';
    } else if (isAe()) {
      app = 'AfterEffects';
    } else if (isPs()) {
      app = 'Photoshop';
    } else if (isAudition()) {
      app = 'Audition';
    }

    const isWindows = (navigator.platform == 'Win32') || (navigator.platform == 'Windows');
    // Adobe applications are either Windows or Mac
    const isMac = !isWindows;

    const nodeApi = getNodeApi(window.cep_node.require);

    const vfs = new LocalCallbackBasedVfs(nodeApi.fs, nodeApi.path);

    const clock = makeNodeJsHighPrecisionMonotonicClock(nodeApi?.process?.hrtime);

    const downloader = createUiDefaultDownloader(vfs, window, backendClient, clock);

    return {
      type: 'adobePanel',
      app,
      isWindows,
      isMac,
      pathSep: getPathSep(isWindows),
      readdir: window.cep.fs.readdir,
      nodeApi,
      cepBridgeClient: new CepBridgeClient(),
      backendClient,
      downloader,
      vfs,
      clientInstanceId: createUuid(),
    };

  }

  const hasElectron = (window && window.mimirElectron) != undefined;
  if (hasElectron) {
    invariant(window.mimirElectron, 'window.mimirElectron required but missing!');
    const mainRequire = window.mimirElectron.mainRequire as DesktopMainProcessRequireType;
    const rendererRequire = window.mimirElectron.rendererRequire;

    const nodeApi = getIpcNodeApi(mainRequire);

    let vfs: Vfs | undefined = undefined;
    // The ipcVfs exposed through electron does not implement vfs due to context isolation
    // in the electron context bridge, so we have to wrap it into a type that implements
    // the actual Vfs interface.
    // For more information see the comment above the IpcVfs interface definition.
    if (window.mimirElectron.ipcVfs !== undefined) {
      const portClientPromise = initVfsPortClient(window.mimirElectron);
      vfs = createIpcVfsWrapper(window.mimirElectron.ipcVfs, portClientPromise);
    }
    if (vfs === undefined) {
      // Instead of relying on the remote module execution we should instead
      // use the IPC based VFS implementation. The problem is that a lot of
      // users are still running older Mimir Desktop versions which does not
      // provide the IPC based VFS implementation. As a fallback we use the
      // callback based fs functions to create a Vfs implementation. NOTE:
      // The regular LocalVfs uses the fs.promises API which the remote module
      // functionality is not able to correctly transfer to the browser
      // context.
      console.warn('Mimir Desktop is outdated. Please update');
      vfs = new LocalCallbackBasedVfs(nodeApi.fs, nodeApi.path);
    }
    configureElectronLogging(mainRequire, rendererRequire);
    const os = window.mimirElectron.mainRequire('os');
    const isWindows = os.platform() === 'win32';
    // Mimir Desktop is either Windows or Mac
    const isMac = !isWindows;

    const dvrClient = new DvrClient();

    const clock = makeNodeJsHighPrecisionMonotonicClock(nodeApi?.process?.hrtime);

    const downloader = createUiDefaultDownloader(vfs, window, backendClient, clock);

    // No fallback for old Mimir Desktop versions, users will have to upgrade
    // to use local drives.
    const localDriveProvider = window.mimirElectron.ipcDriveProvider ?? null;

    const context: ElectronContext = {
      type: 'electron',
      isWindows,
      isMac,
      pathSep: getPathSep(isWindows),
      mainRequire,
      rendererRequire,
      dvrMode: false,
      cepBridgeClient: dvrClient,
      nodeApi,
      backendClient,
      downloader,
      vfs,
      growingFileSessions: {},
      sequenceUploadLock: new Lock(),
      clientInstanceId: createUuid(),
      localDriveProvider,
    };
    dvrClient.setElectronContext(context);
    return context;
  }

  const inIframe = window.location !== window.parent.location;
  const windows = (navigator.platform == 'Win32') || (navigator.platform == 'Windows');
  const mac = (navigator.platform == 'MacIntel') || (navigator.platform == 'Macintosh');
  return {
    type: 'browser',
    inIframe,
    isWindows: windows,
    isMac: mac,
    pathSep: getPathSep(windows),
    nodeApi: null,
    backendClient,
    clientInstanceId: createUuid(),
    prematureVideoFrameReuseBugWorkaround: needPrematureVideoFrameReuseBugWorkaroundFromBrowserContext(),
  };
}


// Replace console logging with electron-log so that we get persistent logs in a disk file
export function configureElectronLogging(
  mainRequire: DesktopMainProcessRequireType,
  rendererRequire: (s: string) => any,
) {
  // Try to get the rendererLogger module which is setup in the main process.
  // It can throw for Mimir Desktop versions < 2.0.0 which do not have a way
  // to update the main process code. We have a fallback to the old way for
  // this case.
  let rendererLogger: IpcRendererLogger | undefined;
  try {
    rendererLogger = mainRequire('./rendererLogger');
  } catch (e) {
    console.error('Could not load rendererLogger', e);
  }

  if (rendererLogger) {
    // rendererLogger actually writes to the log file. We would
    // like to see the logs in the dev console too. This is achieved
    // by the DualLogger below.

    const originalConsoleFunctions = {
      error: console.error,
      warn: console.warn,
      info: console.info,
      debug: console.debug,
      log: console.log,
    };

    const dualConsoleFunctions = {
      error(...params: any[]): void {
        originalConsoleFunctions.error(...params);
        rendererLogger.error(...params);
      },
      warn(...params: any[]): void {
        originalConsoleFunctions.warn(...params);
        rendererLogger.warn(...params);
      },
      info(...params: any[]): void {
        originalConsoleFunctions.info(...params);
        rendererLogger.info(...params);
      },
      debug(...params: any[]): void {
        originalConsoleFunctions.debug(...params);
        // In the future, we can have more levels and numerically compare the level.
        // For now, only toggling the debug level is useful in practice.
        if (desktopState.logLevel === 'debug') {
          rendererLogger.debug(...params);
        }
      },
      log(...params: any[]): void {
        originalConsoleFunctions.log(...params);
        rendererLogger.log(...params);
      }
    };

    Object.assign(console, dualConsoleFunctions);
  } else {
    // Old way
    const rendererLogger = rendererRequire('electron-log');
    rendererLogger.transports.ipc.level = false;    // needed to prevent mixing logs with the other process
    Object.assign(console, rendererLogger.functions);
  }
}
